// src/pages/AdminServiceApprovalPage.jsx

import React, { useEffect, useState } from "react";
import Sidebar from "../components/layout/Sidebar";
import { AUTH_STORAGE_KEY } from "../socket";

function getToken() {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    return raw ? JSON.parse(raw)?.token : null;
  } catch {
    return null;
  }
}

function authHeader() {
  const token = getToken();
  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};
}

function resolveStatus(obj) {
  try {
    const raw = obj?.approvalStatus;
    if (typeof raw === "string") {
      const s = raw.toLowerCase();
      if (s === "approved" || s === "rejected" || s === "pending") return s;
    }
    if (typeof raw === "boolean") {
      return raw ? "approved" : "rejected";
    }
    if (obj?.isApproved) return "approved";
    if (obj?.isRejected) return "rejected";
  } catch (e) {
    // fallthrough
  }
  return "pending";
}

export default function AdminServiceApprovalPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [catalogProcessing, setCatalogProcessing] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [role, setRole] = useState(3);
  const [activeTab, setActiveTab] = useState("services");

  const fetchServices = async () => {
    try {
      setLoading(true);

      const res = await fetch("/api/admin/services", {
        headers: authHeader(),
      });

      const data = await res.json();

      setServices(data.services || []);
    } catch (err) {
      console.error(err);
      alert("Failed to load services");
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchServices();
  }, []);

 useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 640);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
      try {
        const raw = localStorage.getItem(AUTH_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        const user = parsed?.user || {};
        setRole(Number(user.role) || (user.isAdmin ? 2 : 3));
      } catch (err) {
        setRole(3);
      }
    }, []);
  

  const approveService = async (id) => {
    try {
      setProcessing(id);

      const res = await fetch(
        `/api/admin/services/${id}/approve`,
        {
          method: "PUT",
          headers: authHeader(),
        }
      );

      const data = await res.json();

      if (data.success) {
        alert("Service Approved");
        fetchServices();
      } else {
        alert(data.error || "Approval failed");
      }
    } catch (err) {
      console.error(err);
      alert("Approval failed");
    }

    setProcessing(null);
  };

  const deleteService = async (id) => {
    const ok = window.confirm("Are you sure you want to delete this service?");

    if (!ok) return;

    try {
      setProcessing(id);

      const res = await fetch(`/api/admin/services/${id}`, {
        method: "DELETE",
        headers: authHeader(),
      });

      const data = await res.json();

      if (data.success) {
        alert("Service Deleted");
        fetchServices();
      } else {
        alert(data.error || data.message || "Delete failed");
      }
    } catch (err) {
      console.error(err);
      alert("Delete failed");
    }

    setProcessing(null);
  };

  const approveCatalogItem = async (serviceId, index) => {
    try {
      setCatalogProcessing(`${serviceId}-${index}`);
      const res = await fetch(`/api/admin/services/${serviceId}/catalog/${index}/approve`, {
        method: "PUT",
        headers: authHeader(),
      });
      const data = await res.json();
      if (data.success) {
        alert("Catalog item approved");
        fetchServices();
      } else {
        alert(data.error || data.message || "Approval failed");
      }
    } catch (err) {
      console.error(err);
      alert("Approval failed");
    }
    setCatalogProcessing(null);
  };

  const rejectCatalogItem = async (serviceId, index) => {
    try {
      setCatalogProcessing(`${serviceId}-${index}`);
      const res = await fetch(`/api/admin/services/${serviceId}/catalog/${index}/reject`, {
        method: "PUT",
        headers: authHeader(),
      });
      const data = await res.json();
      if (data.success) {
        alert("Catalog item rejected");
        fetchServices();
      } else {
        alert(data.error || data.message || "Rejection failed");
      }
    } catch (err) {
      console.error(err);
      alert("Rejection failed");
    }
    setCatalogProcessing(null);
  };

  const rejectService = async (id) => {
    const ok = window.confirm(
      "Are you sure you want to reject this service?"
    );

    if (!ok) return;

    try {
      setProcessing(id);

      const res = await fetch(
        `/api/admin/services/${id}/reject`,
        {
          method: "PUT",
          headers: authHeader(),
        }
      );

      const data = await res.json();

      if (data.success) {
        alert("Service Rejected");
        fetchServices();
      } else {
        alert(data.error || "Reject failed");
      }
    } catch (err) {
      console.error(err);
      alert("Reject failed");
    }

    setProcessing(null);
  };

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        background: "#f8fafc",
      }}
    >
       {!isMobile && <Sidebar role={role} />}
      <div
        style={{
          flex: 1,
          padding: "24px",
          overflow: "auto",
        }}
      >
        <div
          style={{
            background: "#fff",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 2px 10px rgba(0,0,0,.08)",
            marginBottom: "20px",
          }}
        >
          <h2 style={{ margin: 0, color: "#0f172a" }}>Approval Management</h2>
          <p style={{ marginTop: 8, color: "#64748b" }}>
            Manage both service approvals and catalog approvals from one screen.
          </p>

          <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
            <button
              onClick={() => setActiveTab("services")}
              style={{
                border: "none",
                borderBottom: activeTab === "services" ? "3px solid #0ea5e9" : "3px solid transparent",
                background: activeTab === "services" ? "#f0f9ff" : "#f8fafc",
                color: activeTab === "services" ? "#0369a1" : "#64748b",
                padding: "10px 14px",
                borderRadius: "8px 8px 0 0",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Service Approval
            </button>
            <button
              onClick={() => setActiveTab("catalog")}
              style={{
                border: "none",
                borderBottom: activeTab === "catalog" ? "3px solid #0ea5e9" : "3px solid transparent",
                background: activeTab === "catalog" ? "#f0f9ff" : "#f8fafc",
                color: activeTab === "catalog" ? "#0369a1" : "#64748b",
                padding: "10px 14px",
                borderRadius: "8px 8px 0 0",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Catalog Approval
            </button>
          </div>
        </div>

        {activeTab === "catalog" && (
          <div
            style={{
              background: "#fff",
              padding: "20px",
              borderRadius: "12px",
              boxShadow: "0 2px 10px rgba(0,0,0,.08)",
              marginBottom: "20px",
            }}
          >
            <p style={{ margin: 0, color: "#64748b" }}>
              Approve or reject catalog items before they appear on the landing page.
            </p>
            <div style={{ marginTop: 16, display: "grid", gap: 12 }}>
              {services.filter((service) => Array.isArray(service.catalogItems) && service.catalogItems.length > 0).length === 0 ? (
                <div style={{ padding: 16, border: "1px dashed #cbd5e1", borderRadius: 10, color: "#64748b" }}>
                  No catalog items available for review.
                </div>
              ) : (
                services
                  .filter((service) => Array.isArray(service.catalogItems) && service.catalogItems.length > 0)
                  .map((service) => (
                    <div key={service.id || service._id} style={{ border: "1px solid #e2e8f0", borderRadius: 12, padding: 14 }}>
                      <div style={{ fontWeight: 700, marginBottom: 10 }}>{service.businessName || service.category}</div>
                      <div style={{ display: "grid", gap: 10 }}>
                        {(service.catalogItems || []).map((item, index) => {
                          const status = resolveStatus(item);
                          const badgeStyle = status === "approved"
                            ? { background: "#dcfce7", color: "#166534" }
                            : status === "rejected"
                              ? { background: "#fee2e2", color: "#991b1b" }
                              : { background: "#fef3c7", color: "#92400e" };
                          return (
                            <div key={`${service.id || service._id}-${index}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: 10, background: "#f8fafc", borderRadius: 10 }}>
                              <div>
                                <div style={{ fontWeight: 600 }}>{item.title || item.name || `Catalog Item ${index + 1}`}</div>
                                <div style={{ fontSize: 12, color: "#64748b" }}>{item.category || "-"} • ₹{item.price || "0"}</div>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ ...badgeStyle, padding: "4px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600 }}>
                                  {status === "approved" ? "Approved" : status === "rejected" ? "Rejected" : "Pending"}
                                </span>
                                {status !== "approved" && (
                                  <button
                                    onClick={() => approveCatalogItem(service.id || service._id, index)}
                                    disabled={catalogProcessing === `${service.id || service._id}-${index}`}
                                    style={{ background: "#16a34a", color: "#fff", border: "none", padding: "7px 12px", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}
                                  >
                                    Approve
                                  </button>
                                )}
                                {status !== "rejected" && (
                                  <button
                                    onClick={() => rejectCatalogItem(service.id || service._id, index)}
                                    disabled={catalogProcessing === `${service.id || service._id}-${index}`}
                                    style={{ background: "#dc2626", color: "#fff", border: "none", padding: "7px 12px", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}
                                  >
                                    Reject
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>
        )}

        {activeTab === "services" && loading ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px",
              fontSize: "16px",
            }}
          >
            Loading Services...
          </div>
        ) : activeTab === "services" ? (
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 2px 10px rgba(0,0,0,.08)",
            }}
          >
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "#f1f5f9",
                    }}
                  >
                    <th style={thStyle}>Business</th>
                    <th style={thStyle}>Category</th>
                    <th style={thStyle}>Phone</th>
                    <th style={thStyle}>Area</th>
                    <th style={thStyle}>Certificate</th>
                    <th style={thStyle}>License</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {services.length === 0 ? (
                    <tr>
                      <td
                        colSpan="8"
                        style={{
                          textAlign: "center",
                          padding: "50px",
                          color: "#64748b",
                        }}
                      >
                        No Services Found
                      </td>
                    </tr>
                  ) : (
                    services.map((service) => {
                      const status = resolveStatus(service);
                      return (
                      <tr
                        key={service.id || service._id}
                        style={{
                          borderBottom:
                            "1px solid #e2e8f0",
                        }}
                      >
                        <td style={tdStyle}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                            }}
                          >
                            {service.photoUrl && (
                              <img
                                src={service.photoUrl}
                                alt=""
                                style={{
                                  width: 50,
                                  height: 50,
                                  borderRadius: 8,
                                  objectFit: "cover",
                                }}
                              />
                            )}

                            <div>
                              <div
                                style={{
                                  fontWeight: 600,
                                }}
                              >
                                {service.businessName}
                              </div>

                              <div
                                style={{
                                  fontSize: 12,
                                  color: "#64748b",
                                }}
                              >
                                {service.ownerId}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td style={tdStyle}>
                          {service.category}
                        </td>

                        <td style={tdStyle}>
                          {service.phone}
                        </td>

                        <td style={tdStyle}>
                          {service.area}
                        </td>

                        <td style={tdStyle}>
                          {service.certificateUrl ? (
                            <a
                              href={service.certificateUrl}
                              target="_blank"
                              rel="noreferrer"
                              style={linkStyle}
                            >
                              View Certificate
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>

                        <td style={tdStyle}>
                          {service.licenseUrl ? (
                            <a
                              href={service.licenseUrl}
                              target="_blank"
                              rel="noreferrer"
                              style={linkStyle}
                            >
                              View License
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>

                        <td style={tdStyle}>
                          {status === "approved" ? (
                            <span style={{ background: "#dcfce7", color: "#166534", padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                              Approved
                            </span>
                          ) : status === "rejected" ? (
                            <span style={{ background: "#fee2e2", color: "#991b1b", padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                              Rejected
                            </span>
                          ) : (
                            <span style={{ background: "#fef3c7", color: "#92400e", padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                              Pending Approval
                            </span>
                          )}
                        </td>

                        <td style={tdStyle}>
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                            }}
                          >
                            {status !== "approved" && (
  <button
    onClick={() =>
      approveService(service.id || service._id)
    }
    disabled={processing === (service.id || service._id)}
    style={{
      background: "#16a34a",
      color: "#fff",
      border: "none",
      padding: "8px 14px",
      borderRadius: 6,
      cursor: "pointer",
      fontWeight: 600,
    }}
  >
    {status === "rejected" ? "Approve Again" : "Approve"}
  </button>
)}
                            <button
                              onClick={() =>
                                rejectService(
                                  service.id ||
                                    service._id
                                )
                              }
                              disabled={
                                processing ===
                                (service.id ||
                                  service._id)
                              }
                              style={{
                                background:
                                  "#dc2626",
                                color: "#fff",
                                border: "none",
                                padding:
                                  "8px 14px",
                                borderRadius: 6,
                                cursor:
                                  "pointer",
                                fontWeight: 600,
                              }}
                            >
                              Reject
                            </button>

                            <button
                              onClick={() =>
                                deleteService(
                                  service.id ||
                                    service._id
                                )
                              }
                              disabled={
                                processing ===
                                (service.id ||
                                  service._id)
                              }
                              style={{
                                background:
                                  "#111827",
                                color: "#fff",
                                border: "none",
                                padding:
                                  "8px 14px",
                                borderRadius: 6,
                                cursor:
                                  "pointer",
                                fontWeight: 600,
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    )})
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
       {/* <MobileBottomNav /> */}

    </div>
  );
}

const thStyle = {
  padding: "14px",
  textAlign: "left",
  fontSize: "14px",
  color: "#334155",
  fontWeight: 700,
};

const tdStyle = {
  padding: "14px",
  color: "#0f172a",
  fontSize: "14px",
};

const linkStyle = {
  color: "#0284c7",
  textDecoration: "none",
  fontWeight: 600,
};